// src/components/merchant/InfoSectionsEditor.js
// Sections d'information libres (onglets de la fiche produit)
// Maxula — AbodyStudio Limited

import React from 'react';
import { FiPlus, FiTrash2, FiChevronUp, FiChevronDown, FiFileText } from 'react-icons/fi';

const MAX_SECTIONS = 8;

// Intitulés courants, proposés en un clic. Ce ne sont que des raccourcis : le
// marchand peut écrire ce qu'il veut. Des rubriques figées dans le code
// n'auraient aucun sens pour un spa ou un séjour.
const SUGGESTIONS = [
  "Conseils d'utilisation",
  'Ingrédients',
  "Précautions d'emploi",
  'Composition',
  'Bienfaits',
  'Livraison et retours',
];

const InfoSectionsEditor = ({ value = [], onChange }) => {
  const sections = Array.isArray(value) ? value : [];

  const update = (i, patch) =>
    onChange(sections.map((s, k) => (k === i ? { ...s, ...patch } : s)));

  const add = (title = '') => {
    if (sections.length >= MAX_SECTIONS) return;
    onChange([...sections, { title, body: '' }]);
  };

  const remove = (i) => onChange(sections.filter((_, k) => k !== i));

  // L'ordre des sections est l'ordre des onglets : le marchand doit pouvoir
  // mettre « Précautions d'emploi » en dernier et « Ingrédients » en premier.
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    const next = [...sections];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  const used = new Set(sections.map((s) => String(s.title || '').toLowerCase()));

  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <div className="p-6 border-b">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <FiFileText /> Informations détaillées (facultatif)
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Chaque section devient un onglet sur la fiche, à côté de « Description ».
          Une section laissée vide n'apparaît pas.
        </p>
      </div>

      <div className="p-6">
        {sections.length === 0 && (
          <p className="text-sm text-gray-500 mb-4">
            Aucune section : la fiche affichera seulement la description.
          </p>
        )}

        {sections.map((sec, i) => (
          <div key={i} className="border rounded-lg mb-4">
            <div className="flex items-center gap-2 p-3 border-b bg-gray-50 rounded-t-lg">
              <input
                type="text"
                value={sec.title}
                maxLength={60}
                onChange={(e) => update(i, { title: e.target.value })}
                placeholder="Titre de l'onglet (ex : Ingrédients)"
                className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm font-semibold"
              />
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                className="p-2 rounded hover:bg-gray-200 disabled:opacity-30" title="Monter">
                <FiChevronUp size={15} />
              </button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === sections.length - 1}
                className="p-2 rounded hover:bg-gray-200 disabled:opacity-30" title="Descendre">
                <FiChevronDown size={15} />
              </button>
              <button type="button" onClick={() => remove(i)}
                className="p-2 rounded text-red-500 hover:bg-red-50" title="Supprimer">
                <FiTrash2 size={15} />
              </button>
            </div>

            <div className="p-3">
              <textarea
                rows={5}
                value={sec.body}
                maxLength={4000}
                onChange={(e) => update(i, { body: e.target.value })}
                placeholder="Texte affiché dans cet onglet. Les retours à la ligne sont conservés."
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
              {(!sec.title || !sec.body) && (
                <p className="text-xs text-amber-700 mt-1">
                  Titre et texte sont tous deux nécessaires — sinon cette section
                  ne sera pas enregistrée.
                </p>
              )}
            </div>
          </div>
        ))}

        {sections.length < MAX_SECTIONS && (
          <>
            <div className="flex flex-wrap gap-2 mb-3">
              {SUGGESTIONS.filter((t) => !used.has(t.toLowerCase())).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => add(t)}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:border-primary-500 hover:text-primary-700"
                >
                  + {t}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => add('')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-primary-600 text-primary-700 text-sm font-semibold hover:bg-primary-50"
            >
              <FiPlus size={14} /> Section personnalisée
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default InfoSectionsEditor;
